import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import { Box, Stack, SwipeableDrawer, Typography } from "@mui/material";
import { UnstyledButton } from "Components/Shared/Buttons/UnstyledButton";
import { ModalDialog } from "Components/Shared/Dialogs/ModalDialog";
import { CloseIcon } from "Components/Shared/Icons";
import { useIsMobile } from "Hooks/useIsMobile";
import { Resources, useResource } from "Translations/Resources";
import { noop } from "Utils/FunctionUtils";
import type { ReactNode } from "react";

type Props = {
  /** Omit for dialogs that render their own centered heading in `children`;
      the header row then holds just the close button. */
  title?: string;
  children: ReactNode;
  onClose: () => void;
  isOpen: boolean;
  onBack?: () => void;
  /** A full-bleed bar below the content — outside the padding and the scrolling. */
  footer?: ReactNode;
  /** Names the dialog when `title` is omitted — see ModalDialog. */
  accessibilityLabel?: string;
};

// Same API as ModalDialog: a centered MUI Dialog on desktop, but a bottom
// sheet (SwipeableDrawer) on mobile. Desktop delegates to ModalDialog so the
// two stay visually identical there.
export const ResponsiveDialog = ({
  title,
  onClose,
  children,
  isOpen,
  onBack,
  footer,
  accessibilityLabel,
}: Props) => {
  const isMobile = useIsMobile();
  const { t } = useResource();

  if (!isMobile) {
    return (
      <ModalDialog
        title={title}
        onClose={onClose}
        isOpen={isOpen}
        onBack={onBack}
        footer={footer}
        accessibilityLabel={accessibilityLabel}
      >
        {children}
      </ModalDialog>
    );
  }

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={isOpen}
      onClose={onClose}
      onOpen={noop}
      disableSwipeToOpen
      disableDiscovery
      slotProps={{
        paper: {
          "aria-label": title ?? accessibilityLabel,
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: "90vh",
            overflowY: "auto",
          },
        },
      }}
      // Modal level, same as the desktop ModalDialog — NOT a hardcoded 10001: selects
      // inside the sheet portal their menus to <body> at the modal z-index, so a sheet
      // above that level paints over its own open dropdowns (the menu becomes invisible
      // and unclickable). At the same level the menu wins as the later sibling.
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
    >
      {/* Padding lives on the individual rows rather than on this Stack, so a
          footer can span the full width of the sheet. */}
      <Stack pt={3} pb={footer ? 0 : 8} gap={3}>
        <Box display="flex" justifyContent="center" px={3}>
          <Box
            aria-hidden
            sx={{ width: 40, height: 4, bgcolor: "grey.300", borderRadius: 2 }}
          />
          <UnstyledButton
            onClick={onClose}
            sx={{ position: "absolute", right: 16, top: 16, cursor: "pointer" }}
          >
            <CloseIcon width={18} height={18} />
          </UnstyledButton>
        </Box>
        {(!!title || !!onBack) && (
          <Stack direction="row" alignItems="center" gap={2} px={3} mr={4}>
            {!!onBack && (
              <UnstyledButton
                onClick={onBack}
                aria-label={t(Resources.Common.Back)}
                sx={{ cursor: "pointer" }}
              >
                <ArrowBackIosNewRoundedIcon fontSize="small" />
              </UnstyledButton>
            )}
            {!!title && <Typography variant="h6">{title}</Typography>}
          </Stack>
        )}
        <Box px={3}>{children}</Box>
        {/* Extra bottom padding keeps the actions clear of the home indicator and
            the app's bottom menu, which the sheet is drawn over. */}
        {!!footer && (
          <Stack
            px={3}
            pt={3}
            pb={6}
            borderTop={1}
            borderColor="divider"
            bgcolor="grey.50"
          >
            {footer}
          </Stack>
        )}
      </Stack>
    </SwipeableDrawer>
  );
};
